import { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqCategories, faqItems } from '../../../mocks/faq';

export default function RosevilleFAQ() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [openItems, setOpenItems] = useState<number[]>([]);

  const filteredFAQs = activeCategory === 'all'
    ? faqItems
    : faqItems.filter((item) => item.category === activeCategory);

  const toggleItem = (id: number) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const getCategoryIcon = (categoryId: string) => {
    const cat = faqCategories.find((c) => c.id === categoryId);
    return cat?.icon || 'ri-question-line';
  };

  const getCategoryLabel = (categoryId: string) => {
    const cat = faqCategories.find((c) => c.id === categoryId);
    return cat?.label || '';
  };

  return (
    <section id="faq" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-50 text-teal-700 text-sm font-semibold rounded-full mb-4">
            <i className="ri-question-answer-line"></i>
            Newcomer Resources
          </span>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Roseville <span className="text-teal-600">FAQ</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Answers to the most common questions from people moving to or visiting Roseville, California
          </p>
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 mt-4 text-teal-600 font-semibold hover:text-teal-700 transition-colors cursor-pointer"
          >
            View All FAQ
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {faqCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-teal-600 text-white shadow-md shadow-teal-200'
                  : 'bg-white text-gray-600 hover:bg-teal-50 hover:text-teal-700 border border-gray-200'
              }`}
            >
              <i className={`${cat.icon} text-base`}></i>
              {cat.label}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto space-y-3">
          {filteredFAQs.map((item) => {
            const isOpen = openItems.includes(item.id);
            return (
              <div
                key={item.id}
                className={`bg-white rounded-xl border transition-all duration-300 ${
                  isOpen
                    ? 'border-teal-200 shadow-lg shadow-teal-50'
                    : 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
                }`}
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="w-full flex items-start gap-4 p-5 text-left cursor-pointer group"
                >
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-lg shrink-0 transition-colors duration-300 ${
                      isOpen ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-500 group-hover:bg-teal-50 group-hover:text-teal-600'
                    }`}
                  >
                    <i className={`${getCategoryIcon(item.category)} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-teal-600 uppercase tracking-wide">
                        {getCategoryLabel(item.category)}
                      </span>
                    </div>
                    <h3
                      className={`text-base font-semibold transition-colors duration-300 pr-4 ${
                        isOpen ? 'text-teal-700' : 'text-gray-900 group-hover:text-teal-700'
                      }`}
                    >
                      {item.question}
                    </h3>
                  </div>
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 mt-1 transition-all duration-300 ${
                      isOpen
                        ? 'bg-teal-600 text-white rotate-180'
                        : 'bg-gray-100 text-gray-500 group-hover:bg-teal-100 group-hover:text-teal-600'
                    }`}
                  >
                    <i className="ri-arrow-down-s-line text-lg"></i>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-400 ease-in-out ${
                    isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-5 pb-5 pl-[4.75rem]">
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-10 text-center text-white">
          <div className="max-w-2xl mx-auto">
            <i className="ri-chat-smile-3-line text-5xl mb-4 block"></i>
            <h3 className="text-2xl font-bold mb-3">Still Have Questions?</h3>
            <p className="text-teal-100 mb-6 text-base">
              Check out our comprehensive Relocation Guide or reach out — we&#39;re happy to help newcomers feel at home in Roseville.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/relocation"
                className="bg-white text-teal-700 px-8 py-3 rounded-full font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-compass-3-line"></i>
                Relocation Guide
              </Link>
              <a
                href="mailto:info@roseville.ca.us"
                className="bg-teal-800 text-white px-8 py-3 rounded-full font-semibold hover:bg-teal-900 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
              >
                <i className="ri-mail-send-line"></i>
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
